import { useState, useEffect } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import CreateRole from '../../components/CreateRole/CreateRole'
import { rolesApi, RoleCreatePayload } from '@/features/role/api/roles.api'
import { rolesKeys } from '@/features/role/api/roles.keys'
import { authApi } from '@/features/auth'
import { PermissionModule } from '../../types'
import styles from '../RolePage/Rolepage.module.scss'
import { Loader } from '@/shared/components/Loader/Loader'

type Props = {
  mode: 'create' | 'edit'
  roleId?: string
}

export default function RoleFormPage({ mode, roleId }: Props) {
  const navigate = useNavigate()

  const [createRoleExpanded, setCreateRoleExpanded] = useState(true)
  const [newRoleName, setNewRoleName] = useState('')
  const [newRoleDescription, setNewRoleDescription] = useState('')
  const [isCreateLoading, setIsCreateLoading] = useState(false)
  const [selectedModules, setSelectedModules] = useState<string[]>([])
  const [modules, setModules] = useState<PermissionModule[]>([])
  const [createRoleModulesPayload, setCreateRoleModulesPayload] = useState<
    RoleCreatePayload['modules']
  >([])
  const [initialSelectedPermissionKeys, setInitialSelectedPermissionKeys] = useState<string[]>([])

  const isEditMode = mode === 'edit'
  const editingRoleId = isEditMode ? roleId ?? null : null

  const { data: tenantModules = [], isLoading: tenantModulesLoading } = useQuery({
    queryKey: ['auth', 'tenantModules'],
    queryFn: authApi.getTenantModules,
    staleTime: 5 * 60 * 1000,
  })

  const { data: roleDetails, refetch: refetchRoleDetails } = useQuery({
    queryKey: editingRoleId ? rolesKeys.roleById(editingRoleId) : ['roles', 'roleById', 'none'],
    queryFn: () => rolesApi.getRoleById(editingRoleId ?? ''),
    enabled: Boolean(editingRoleId) && isEditMode,
    staleTime: 5 * 60 * 1000,
  })

  useEffect(() => {
    if (isEditMode && editingRoleId) {
      refetchRoleDetails()
    }
  }, [isEditMode, editingRoleId, refetchRoleDetails])

  useEffect(() => {
    if (tenantModulesLoading || !tenantModules.length) return

    const normalizedModules: PermissionModule[] = tenantModules.map((m) => ({
      id: String(m.module_id),
      name: m.module_name,
      expanded: false,
      selected: false,
      features: [],
    }))

    setModules(normalizedModules)
  }, [tenantModules, tenantModulesLoading])

  useEffect(() => {
    if (!roleDetails) return

    setNewRoleName(roleDetails.role_name ?? (roleDetails as any).label ?? '')
    setNewRoleDescription(roleDetails.role_description ?? '')

    const rawAccess: any[] =
      (roleDetails as any).access ??
      (roleDetails as any).access_structure ??
      (roleDetails as any).accessStructure ??
      []

    const modulesAccess: any[] = Array.isArray((roleDetails as any).modules)
      ? (roleDetails as any).modules
      : []

    // Back-compat: old endpoint returned a flat `access` array.
    if (rawAccess.length > 0) {
      const selectedModuleIds = Array.from(
        new Set(
          rawAccess
            .map((item) => item.module_id ?? item.moduleId)
            .filter((v) => v !== undefined && v !== null)
            .map((v) => String(v))
        )
      )
      setSelectedModules(selectedModuleIds)

      const allowedPermissionKeys = rawAccess
        .filter((item) => item.is_allowed ?? item.isAllowed ?? item.allowed)
        .map((item) => {
          const featureId = item.feature_id ?? item.featureId
          const permissionId = item.permission_id ?? item.permissionId
          return `${featureId}-${permissionId}`
        })
        .filter(
          (key) =>
            !key.startsWith('undefined-') && !key.endsWith('-undefined')
        )
      setInitialSelectedPermissionKeys(allowedPermissionKeys)
      return
    }

    // New endpoint returns access structure under `modules`.
    if (modulesAccess.length > 0) {
      const selectedModuleIds = Array.from(
        new Set(
          modulesAccess
            .map((m) => m.module_id ?? m.moduleId)
            .filter((v) => v !== undefined && v !== null)
            .map((v) => String(v))
        )
      )
      setSelectedModules(selectedModuleIds)

      const allowedPermissionKeys = modulesAccess.flatMap((m) => {
        const features = Array.isArray(m.features) ? m.features : []
        return features.flatMap((f: any) => {
          const permissions = Array.isArray(f.permissions) ? f.permissions : []
          return permissions
            .filter((p: any) => p.is_allowed ?? p.isAllowed ?? p.allowed)
            .map(
              (p: any) =>
                `${f.feature_id ?? f.featureId}-${p.permission_id ?? p.permissionId}`
            )
        })
      }).filter(
        (key) => !key.startsWith('undefined-') && !key.endsWith('-undefined')
      )

      setInitialSelectedPermissionKeys(allowedPermissionKeys)
    }
  }, [roleDetails])

  const createRoleMutation = useMutation<any, any, RoleCreatePayload>({
    mutationFn: rolesApi.createRole,
    onMutate: () => setIsCreateLoading(true),
    onSuccess: () => {
      setIsCreateLoading(false)
      navigate('/roles/active')
    },
    onError: (err: any) => {
      console.error('Save role failed', err)
      setIsCreateLoading(false)
    },
  })

  const handleModuleSelection = (moduleId: string, selected: boolean) => {
    setSelectedModules((prev) =>
      selected ? [...new Set([...prev, moduleId])] : prev.filter((id) => id !== moduleId)
    )
    setModules((prev) => prev.map((m) => (m.id === moduleId ? { ...m, selected } : m)))
  }

  const handleCreateRole = () => {
    if (!newRoleName.trim() || !newRoleDescription.trim()) {
      return
    }

    if (!selectedModules.length) {
      return
    }

    const modulesPayload = createRoleModulesPayload.filter((module) =>
      selectedModules.includes(String(module.module_id))
    )

    createRoleMutation.mutate({
      role_id: isEditMode && editingRoleId ? Number(editingRoleId) : undefined,
      label: newRoleName.trim(),
      role_description: newRoleDescription.trim(),
      modules: modulesPayload,
      modified_by: 'tenant_admin',
    })
  }

  const resetAndBack = () => {
    navigate('/roles/active')
  }

  if (tenantModulesLoading && !modules.length) {
    return <Loader text="Loading role form..." />
  }

  return (
    <div>
      <div className={styles.tableCard}>
        <button type="button" className={styles.backBtn} onClick={resetAndBack}>
          <span>‹ Back to Roles</span>
        </button>
        <div className={styles.roleWrapper}>
          <CreateRole
            roleName={newRoleName}
            roleDescription={newRoleDescription}
            title={isEditMode ? 'Edit Role' : 'Create Role'}
            expanded={createRoleExpanded}
            modules={modules}
            selectedModuleIds={selectedModules}
            onRoleNameChange={setNewRoleName}
            onRoleDescriptionChange={setNewRoleDescription}
            onModuleChange={handleModuleSelection}
            onSelectAll={(selected) => {
              setSelectedModules(selected ? modules.map((m) => m.id) : [])
              setModules((prev) => prev.map((m) => ({ ...m, selected })))
            }}
            onToggleExpand={() => setCreateRoleExpanded((prev) => !prev)}
            onModulesPayloadChange={setCreateRoleModulesPayload}
            initialSelectedPermissionKeys={initialSelectedPermissionKeys}
          />
        </div>

        <div className={styles.formActions}>
          <button
            type="button"
            className={styles.cancelBtn}
            onClick={() => {
              setNewRoleName('')
              setNewRoleDescription('')
              setSelectedModules([])
              setCreateRoleModulesPayload([])
              setInitialSelectedPermissionKeys([])
              resetAndBack()
            }}
            disabled={isCreateLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.saveBtn}
            onClick={handleCreateRole}
            disabled={
              isCreateLoading ||
              !newRoleName.trim() ||
              !newRoleDescription.trim() ||
              !selectedModules.length
            }
          >
            {isCreateLoading ? 'Saving...' : isEditMode ? 'Update Role' : 'Create Role'}
          </button>
        </div>
      </div>
    </div>
  )
}
